import { unwrap, type Me, type OutletBootstrap } from '@qrkey/api-client';
import { AppShell, Button, Spinner, Topbar, useToast } from '@qrkey/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useEffect } from 'react';
import { api, onSessionEnded, useSession } from '../app/api.js';
import { pickedOutletStore, resolveOutlet, usePickedOutlet } from '../app/outlet.js';
import { useTerminal } from '../app/terminal.js';
import { navItems, outletPermissions, shortId } from '../lib/permissions.js';
import { signOut } from '../lib/sign-out.js';
import { OfflineBanner } from '../offline/OfflineBanner.js';
import { prepareOffline, readBootstrap, readMe } from '../offline/shell-reads.js';
import { isOfflineSession } from '../offline/session-core.js';

export function useMe() {
  const session = useSession();
  return useQuery<Me>({
    queryKey: ['me', session?.session.id],
    queryFn: () => (session ? readMe(session) : unwrap(api.client.GET('/v1/me'))),
    enabled: session !== null,
  });
}

/** Outlets this person can work at, with names (for the picker). */
export function useOutlets(enabled: boolean) {
  const session = useSession();
  return useQuery({
    queryKey: ['outlets', session?.session.id],
    queryFn: () => unwrap(api.client.GET('/v1/outlets')),
    enabled: session !== null && enabled,
  });
}

/**
 * The till's bundle for the current outlet (GET {o}/bootstrap). The browser
 * revalidates it with its ETag, so refetching an unchanged bundle is a 304.
 */
export function useBootstrap(outletId: string | null) {
  const session = useSession();
  return useQuery<OutletBootstrap>({
    queryKey: ['bootstrap', outletId, session?.session.id],
    queryFn: () =>
      session
        ? readBootstrap(outletId ?? '', session)
        : unwrap(
            api.client.GET('/v1/outlets/{outletId}/bootstrap', {
              params: { path: { outletId: outletId ?? '' } },
            }),
          ),
    enabled: session !== null && outletId !== null,
  });
}

/** The outlet this session works at: the terminal's, else the one picked in the top bar. */
export function useCurrentOutlet(me: Me | undefined): [string | null, (id: string) => void] {
  const session = useSession();
  const picked = usePickedOutlet();
  const available = me?.outlets.map((o) => o.outletId) ?? [];
  return [resolveOutlet(session?.session.outletId, picked, available), pickedOutletStore.set];
}

// Popups are for the tills: loaded on the work screens only.
const PopupHost = lazy(() =>
  import('../notifications/PopupHost.js').then((m) => ({ default: m.PopupHost })),
);

// Loaded only on a terminal that owns printers: other devices never download it.
const PrintRelayHost = lazy(() =>
  import('../printing/PrintRelayHost.js').then((m) => ({ default: m.PrintRelayHost })),
);

/** Signed-in frame: who and where, navigation by permission, appearance, sign out. */
export function Shell() {
  const session = useSession();
  const terminal = useTerminal();
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  // POS, captain and KDS use the whole screen: navigation becomes a thin strip on top.
  const work = useRouterState({
    select: (st) => /^\/(pos|captain|kds|till)(\/|$)/.test(st.location.pathname),
  });
  const [outletId, setOutletId] = useCurrentOutlet(me.data);
  const picker = !session?.session.outletId && (me.data?.outlets.length ?? 0) > 1;
  const bootstrap = useBootstrap(outletId);
  const named = useOutlets(picker);
  const names = new Map((named.data?.data ?? []).map((o) => [o.id, o.name]));
  const outletName = bootstrap.data?.outlet.name ?? (outletId ? names.get(outletId) : undefined);

  useEffect(
    () =>
      onSessionEnded(() => {
        toast.show({
          tone: 'warning',
          title: 'You were signed out',
          description: 'Sign in again to continue.',
        });
      }),
    [toast],
  );

  useEffect(() => {
    if (!session) void navigate({ to: terminal ? '/pin' : '/login' });
  }, [session, terminal, navigate]);

  // A till with a real session keeps what it needs to carry on when the cloud goes away:
  // staff PIN verifiers, a run of invoice numbers and of KOT numbers (M4 tasks 4 and 5).
  const tillOutlet = session?.session.surface === 'till' ? session.session.outletId : null;
  const tillId = session?.session.terminalId ?? null;
  const realSession = session !== null && !isOfflineSession(session);
  useEffect(() => {
    if (tillOutlet && tillId && realSession) void prepareOffline(tillOutlet, tillId);
  }, [tillOutlet, tillId, realSession, bootstrap.dataUpdatedAt]);

  if (!session) return null;
  const perms = outletPermissions(me.data, outletId);
  const items = navItems(perms, { till: session.session.surface === 'till' });
  const role =
    me.data?.memberships.find((m) => m.outletId === outletId)?.roleName ??
    me.data?.memberships.find((m) => m.outletId === null)?.roleName;
  const outlets = me.data?.outlets ?? [];
  // This device prints the jobs of the printers it owns (03-modules.md §5).
  const boundTerminal =
    terminal && session.session.terminalId === terminal.terminalId ? terminal.terminalId : null;
  const ownPrinters = new Map(
    (bootstrap.data?.printers ?? [])
      .filter((p) => boundTerminal !== null && p.ownerTerminalId === boundTerminal)
      .map((p) => [p.id, p.name]),
  );

  return (
    <AppShell
      {...(work ? { className: 'shell--work' } : {})}
      topbar={
        <Topbar
          brand={
            <Link to="/" className="brand">
              <span className="brand-mark" aria-hidden="true">
                Q
              </span>
              <span>QRkey</span>
            </Link>
          }
          context={
            <span className="topbar-context" data-testid="shell-context">
              {!picker ? (
                <span>{outletName ?? (outletId ? `Outlet ${shortId(outletId)}` : 'Outlet –')}</span>
              ) : (
                <label className="outlet-picker">
                  <span className="qk-sr-only">Outlet</span>
                  <select value={outletId ?? ''} onChange={(e) => setOutletId(e.target.value)}>
                    {outlets.map((o) => (
                      <option key={o.outletId} value={o.outletId}>
                        {names.get(o.outletId) ?? `Outlet ${shortId(o.outletId)}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {terminal && session.session.terminalId === terminal.terminalId ? (
                <span> · {terminal.name}</span>
              ) : null}
              <span className="qk-badge">
                {session.session.surface === 'till' ? 'Till' : 'Back office'}
              </span>
            </span>
          }
          actions={
            <>
              <span className="user" data-testid="shell-user">
                {me.data ? (
                  <>
                    <span className="user__name">{me.data.user.name}</span>
                    {role ? <span className="user__role">{role}</span> : null}
                  </>
                ) : (
                  <Spinner size="sm" label="Loading your profile" />
                )}
              </span>
              {terminal && session.session.surface === 'till' ? (
                <Link to="/pin" className="qk-btn qk-btn--secondary qk-btn--md">
                  <span className="qk-btn__label">Switch user</span>
                </Link>
              ) : null}
              <Button
                variant="ghost"
                onClick={() => {
                  void signOut().then(() => navigate({ to: terminal ? '/pin' : '/login' }));
                }}
              >
                Sign out
              </Button>
            </>
          }
        />
      }
      nav={
        <>
          {items.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              className="qk-navlink"
              activeProps={{ 'aria-current': 'page' }}
            >
              {i.label}
            </Link>
          ))}
          {perms.has('terminals.register') && session.session.surface === 'backoffice' ? (
            <Link
              to="/setup/terminal"
              className="qk-navlink"
              activeProps={{ 'aria-current': 'page' }}
            >
              Set up this device
            </Link>
          ) : null}
          <Link
            to="/settings/appearance"
            className="qk-navlink"
            activeProps={{ 'aria-current': 'page' }}
          >
            Appearance
          </Link>
          {/* At the end of the navigation, so it never covers a screen's own buttons. */}
          {outletId &&
          boundTerminal &&
          ownPrinters.size > 0 &&
          perms.has('kitchen.print_device') ? (
            <span className="nav-end">
              <Suspense fallback={null}>
                <PrintRelayHost
                  outletId={outletId}
                  terminalId={boundTerminal}
                  printers={ownPrinters}
                />
              </Suspense>
            </span>
          ) : null}
        </>
      }
    >
      <OfflineBanner outletId={outletId} />
      {work && outletId && perms.has('orders.view') ? (
        <Suspense fallback={null}>
          <PopupHost outletId={outletId} />
        </Suspense>
      ) : null}
      <Outlet />
    </AppShell>
  );
}
